(() => {
  'use strict';
  if (customElements.get('trc-premade-version')) return;
  // The order-export Worker on the store's own tracker domain (U-7). It still names snapshot files
  // on its workers.dev address, which the preview reads from this domain (trc-premade-preview.js).
  const WORKER = 'https://track.tablereadycards.com';
  const SNAPSHOT_ORIGINS = [WORKER, 'https://order-export.evponix.workers.dev'];
  const pending = new Map(), known = new Map(), originalDeckLinks = new WeakMap();
  const revisionPattern = /^[A-Za-z0-9_-]{1,200}$/;
  const propertyNames = ['_Input method', '_TRC reorder', '_Reorder decklist URL', '_Reorder profile'];
  const selector = name => `[name="properties[${name}]"]`;
  // AbortSignal.timeout needs iOS 16+; older Safari still buys premade decks.
  function timeoutSignal(milliseconds) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(milliseconds);
    if (typeof AbortController !== 'function') return undefined;
    const controller = new AbortController();
    setTimeout(() => controller.abort(), milliseconds);
    return controller.signal;
  }

  // Raw browser errors ("Load failed", "Failed to fetch") as plain copy (trc-compat.js).
  function friendlyError(error, fallback) {
    const compat = window.TRCCompat;
    if (compat && typeof compat.friendlyErrorMessage === 'function') return compat.friendlyErrorMessage(error, fallback);
    return (error && error.message) || fallback;
  }
  const CHECK_FAILED = 'This deck could not be checked. Please try again.';
  // Card sets (data-noun="set", snippets/trc-product-noun.liquid) say "set" where decks say "deck".
  const forNoun = (text, noun) => noun === 'set' ? String(text).replace(/\bdeck\b/g, 'set').replace(/\bDeck\b/g, 'Set') : text;

  function validate(data, variantId) {
    if (!data || data.ok !== true || typeof data.managed !== 'boolean') throw new Error('This deck could not be checked. Please try again.');
    if (!data.managed) return data;
    const active = data.active;
    if (!active || !revisionPattern.test(active.revision || '') || typeof data.isReorder !== 'boolean') throw new Error('This deck is being prepared. Please try again shortly.');
    const url = new URL(active.previewXmlUrl || active.decklistSnapshotUrl || '');
    if (!SNAPSHOT_ORIGINS.includes(url.origin) || url.pathname !== `/premade-decklists/snapshots/${variantId}/${active.revision}.xml` || url.search || url.hash || url.username || url.password) throw new Error('This deck preview could not be verified. Please try again.');
    return data;
  }

  async function read(variantId) {
    if (!/^\d+$/.test(variantId)) throw new Error('Choose a deck option before adding it to your cart.');
    if (!pending.has(variantId)) {
      const request = (async () => {
        const response = await fetch(`${WORKER}/premade-decklists/variant/${variantId}`, {
          credentials: 'omit', cache: 'no-store', signal: timeoutSignal(20000),
        });
        if (!response.ok) throw new Error('This deck could not be checked. Please try again.');
        const data = validate(await response.json(), variantId);
        known.set(variantId, data);
        document.dispatchEvent(new CustomEvent('trc:premade-version', {detail: {variantId, ...data}}));
        return data;
      })();
      pending.set(variantId, request);
      request.finally(() => pending.delete(variantId)).catch(() => {});
    }
    return pending.get(variantId);
  }

  // The pin and print-run markers a premade line carries for this answer: the
  // product forms and the cart's "Update it in your cart" button
  // (trc-premade-cart-refresh.js) both build them here, so they never differ.
  function lineProperties(data, reorderEnabled) {
    if (!data?.managed) return null;
    const properties = {'_TRC deck version': data.active.revision};
    if (data.isReorder && reorderEnabled) {
      const values = ['Premade reorder', 'true', data.active.decklistUrl || '', 'basic'];
      propertyNames.forEach((name, index) => { properties[name] = values[index]; });
    }
    return properties;
  }

  window.TRCPremadeVersions = {getKnown: variantId => known.get(String(variantId)), read, lineProperties, forNoun, propertyNames: propertyNames.slice()};

  function syncInventory(scope, data, reorderEnabled) {
    if (!data?.managed || !Number.isSafeInteger(data.inventoryQuantity)) return;
    const quantity = data.inventoryQuantity, reorder = data.isReorder && reorderEnabled;
    for (const inventory of scope?.querySelectorAll('[data-trc-inventory]') || []) {
      if (inventory.closest('[data-trc-product-id]') !== scope) continue;
      const low = quantity > 0 && quantity <= Number(inventory.dataset.lowStockThreshold);
      const status = quantity > 0 ? low ? 'low-stock' : 'in-stock' : reorder ? 'in-stock' : 'out-of-stock';
      const row = inventory.querySelector('.product__inventory'), text = inventory.querySelector('.product__inventory-text');
      if (!row || !text) continue;
      row.classList.remove('product__inventory--low-stock', 'product__inventory--in-stock', 'product__inventory--out-of-stock');
      row.classList.add(`product__inventory--${status}`);
      let label = quantity > 0 ? low ? inventory.dataset.lowStockLabel : inventory.dataset.inStockLabel : reorder ? 'Available to order for the next print run' : inventory.dataset.outOfStockLabel;
      if (quantity > 0 && inventory.dataset.showQuantity === 'true') label = low ? quantity === 1 ? inventory.dataset.lowStockOneLabel : inventory.dataset.lowStockCountLabel : inventory.dataset.stockCountLabel;
      const parts = String(label || (quantity > 0 ? '__TRC_QUANTITY__ in stock' : 'Out of stock')).split('__TRC_QUANTITY__');
      text.replaceChildren();
      parts.forEach((part, index) => {
        if (index) { const count = document.createElement('strong'); count.textContent = String(quantity); text.append(count); }
        text.append(document.createTextNode(part));
      });
      const progress = inventory.querySelector('progress-bar');
      if (progress) {
        progress.hidden = !low || quantity >= Number(inventory.dataset.lowStockThreshold);
        progress.dataset.value = String(quantity);
        progress.style.setProperty('--percent', `${Math.max(0, Math.min(100, quantity / Number(progress.dataset.max || 1) * 100))}%`);
      }
    }
  }

  class PremadeVersion extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.closest('form');
      if (!this.form) return;
      this.pin = this.querySelector(selector('_TRC deck version'));
      this.notice = this.querySelector('[data-version-notice]');
      this.generation = 0;
      this.onVariant = () => {
        const id = this.variantId();
        if (id === this.selectedId) return;
        this.selectedId = id;
        this.current = null;
        // Buy it now skips the shelf cap, so it is hidden while one applies.
        this.toggleAttribute('data-shelf-capped', this.shelfStock(id) !== null);
        this.pin.disabled = true; this.pin.value = '';
        this.dataset.state = 'loading';
        this.syncBuyNow();
        this.refresh().catch(error => this.message(friendlyError(error, CHECK_FAILED)));
      };
      this.onSubmit = async event => {
        if (this.allowSubmit) { this.allowSubmit = false; return; }
        event.preventDefault(); event.stopImmediatePropagation();
        if (this.submitting) return;
        this.submitting = true;
        const submitter = event.submitter;
        const variantId = this.variantId();
        const reviewed = this.current?.managed ? this.current.active.revision : '';
        const reviewedReorder = this.current?.managed ? this.current.isReorder : null;
        try {
          const data = await this.refresh();
          if (!data || variantId !== this.variantId()) throw new Error('Your deck option changed. Please add the selected option again.');
          if (data.managed && reviewed && reviewed !== data.active.revision) {
            this.message(data.isReorder ? 'This deck was updated for the next print run. Review the updated preview, then add it again.' : 'The updated edition of this deck is now in stock. Review the updated preview, then add it again.', true);
            return;
          }
          if (data.managed && reviewedReorder !== null && reviewedReorder !== data.isReorder) {
            this.message(data.isReorder ? 'This deck is now out of stock and will be made in the next print run. Review the details, then add it again.' : 'This deck is now in stock. Review the current details, then add it again.', true);
            return;
          }
          if (data.managed && data.isReorder && this.dataset.reorderEnabled !== 'true') throw new Error('This deck is currently out of stock.');
          if (data.managed && !data.isReorder) {
            const copies = Number(new FormData(this.form).get('quantity') || 1);
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {cache: 'no-store', credentials: 'same-origin', signal: timeoutSignal(20000)});
            if (!response.ok) throw new Error('Your cart could not be checked. Please try again.');
            const cart = await response.json();
            if (!Array.isArray(cart.items)) throw new Error('Your cart could not be checked. Please try again.');
            const alreadyInCart = cart.items.filter(item => String(item.variant_id) === variantId).reduce((total, item) => total + Number(item.quantity), 0);
            if (!Number.isSafeInteger(data.inventoryQuantity) || !Number.isSafeInteger(copies) || copies < 1 || !Number.isSafeInteger(alreadyInCart) || alreadyInCart < 0 || copies + alreadyInCart > data.inventoryQuantity) {
              throw new Error(`Only ${Math.max(0, Number(data.inventoryQuantity) || 0)} copies of this deck version remain in stock (${alreadyInCart} already in your cart). Lower the quantity and add it again.`);
            }
            if (variantId !== this.variantId()) throw new Error('Your deck option changed. Please add the selected option again.');
          }
          if (!data.managed) await this.checkShelfStock(variantId);
          if (data !== this.current || variantId !== this.variantId()) throw new Error('Your deck option changed. Please review it and add it again.');
          this.message('');
          this.allowSubmit = true;
          // Let the theme's existing cart drawer, quantity checks, and native
          // form submission run with the verified immutable revision attached.
          this.form.requestSubmit(submitter?.isConnected ? submitter : undefined);
        } catch (error) { this.message(friendlyError(error, CHECK_FAILED)); }
        finally { this.submitting = false; this.allowSubmit = false; }
      };
      this.form.addEventListener('submit', this.onSubmit, true);
      this.form.addEventListener('change', this.onVariant);
      document.addEventListener('variant:changed', this.onVariant);
      this.onVariant();
    }
    variantId() { return String(this.form?.querySelector('[name="id"]')?.value || this.dataset.variantId || ''); }
    // Unmanaged CONTINUE premades sell shelf copies until the shelf is empty;
    // only then does the reorder marker (rendered at inventory <= 0) make new
    // copies in the next print run. Like the managed cap above, never add more
    // than the shelf holds, counting stock copies already in the cart.
    shelfStock(variantId) {
      let stock;
      try { stock = JSON.parse(this.querySelector('[data-trc-shelf-stock]')?.textContent || '{}')[variantId]; } catch (_) { stock = null; }
      if (this.dataset.reorderEnabled !== 'true' || !stock || !Number.isSafeInteger(stock.inventory) || stock.inventory <= 0 || typeof stock.decklistUrl !== 'string' || !stock.decklistUrl) return null;
      return stock.inventory;
    }
    async checkShelfStock(variantId) {
      const inventory = this.shelfStock(variantId);
      if (inventory === null) return;
      if ([...this.form.querySelectorAll(selector('_TRC reorder'))].some(input => !input.disabled && input.value === 'true')) return;
      const copies = Number(new FormData(this.form).get('quantity') || 1);
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {cache: 'no-store', credentials: 'same-origin', signal: timeoutSignal(20000)});
      if (!response.ok) throw new Error('Your cart could not be checked. Please try again.');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Your cart could not be checked. Please try again.');
      const alreadyInCart = cart.items.filter(item => String(item.variant_id) === variantId && String(item.properties?.['_TRC reorder'] || '').toLowerCase() !== 'true').reduce((total, item) => total + Number(item.quantity), 0);
      if (!Number.isSafeInteger(copies) || copies < 1 || !Number.isSafeInteger(alreadyInCart) || alreadyInCart < 0 || copies + alreadyInCart > inventory) {
        const remaining = Math.max(0, inventory - (Number.isSafeInteger(alreadyInCart) ? alreadyInCart : 0));
        throw new Error(`Only ${inventory} in stock${alreadyInCart ? ` (${alreadyInCart} already in your cart)` : ''}. ${remaining ? `Lower the quantity to ${remaining} or fewer and add it again.` : 'Every in-stock copy is already in your cart.'} Once these sell out, more can be ordered to be printed.`);
      }
    }
    message(value, link = false) {
      value = forNoun(value || '', this.dataset.noun);
      this.notice.replaceChildren(document.createTextNode(value));
      this.notice.hidden = !value;
      if (value && link) {
        const url = new URL(this.dataset.productUrl || '', location.origin);
        if (url.origin === location.origin && /^\/products\//.test(url.pathname)) {
          url.searchParams.set('variant', this.variantId());
          const anchor = document.createElement('a'); anchor.href = url.href; anchor.textContent = forNoun(' View deck.', this.dataset.noun);
          this.notice.append(anchor);
        }
      }
    }
    async refresh() {
      const generation = ++this.generation, variantId = this.variantId();
      this.pin.disabled = true;
      const data = await read(variantId);
      if (!this.isConnected || generation !== this.generation || variantId !== this.variantId()) return null;
      this.current = data;
      this.dataset.state = data.managed ? 'managed' : 'unmanaged';
      this.syncBuyNow();
      if (!data.managed) { this.pin.value = ''; this.message(''); return data; }
      this.pin.value = data.active.revision; this.pin.disabled = false;
      this.syncAvailability(data);
      for (const name of propertyNames) this.form.querySelectorAll(selector(name)).forEach(input => input.remove());
      const properties = lineProperties(data, this.dataset.reorderEnabled === 'true');
      for (const name of propertyNames) {
        if (!(name in properties)) continue;
        const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${name}]`; input.value = properties[name];
        this.form.append(input);
      }
      this.message('');
      return data;
    }
    // Buy it now skips the version pin and the shelf cap. The :has() rule in
    // trc-premade-version.liquid hides it; this class does the same where
    // :has() is unsupported (Safari < 15.4, Firefox < 121, Samsung < 23).
    syncBuyNow() {
      this.form?.classList.toggle('trc-premade-buy-now-hidden', this.dataset.state !== 'unmanaged' || this.hasAttribute('data-shelf-capped'));
    }
    syncAvailability(data) {
      syncInventory(this.form.closest('[data-trc-product-id]'), data, this.dataset.reorderEnabled === 'true');
      const reorder = data.isReorder && this.dataset.reorderEnabled === 'true';
      const available = data.inventoryQuantity > 0 || reorder;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = !available;
      for (const button of this.form.querySelectorAll('button[name="add"]')) {
        button.disabled = !available;
        if (available) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', 'true');
        const label = button.querySelector('.product-card__atc-text') || button.querySelector(':scope > span');
        if (label) label.textContent = available ? this.dataset.addLabel || 'Add to cart' : this.dataset.soldOutLabel || 'Sold out';
      }
      const panel = this.form.querySelector('.trc-premade-reorder');
      if (panel) panel.hidden = !reorder;
      const note = this.querySelector('[data-restock-note]');
      if (note) note.hidden = !reorder || !!panel;
    }
    disconnectedCallback() {
      this.generation++;
      this.form?.classList.remove('trc-premade-buy-now-hidden');
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.form?.removeEventListener('change', this.onVariant);
      document.removeEventListener('variant:changed', this.onVariant);
      this.ready = false;
    }
  }
  function moxfieldUrl(raw) {
    try {
      const url = new URL(raw);
      if (url.protocol !== 'https:' || url.username || url.password || url.port || !['moxfield.com', 'www.moxfield.com'].includes(url.hostname.toLowerCase()) || !/^\/decks\/[A-Za-z0-9._~-]+\/?$/.test(url.pathname)) return '';
      url.hostname = 'moxfield.com'; url.pathname = url.pathname.replace(/\/$/, ''); url.search = ''; url.hash = '';
      return url.href;
    } catch (_) { return ''; }
  }

  class PremadeDeckLinks extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.selectedId = null;
      this.anchor = this.querySelector('[data-current-deck-link]');
      this.legacy = JSON.parse(this.querySelector('[data-decklist-links]')?.textContent || '{}');
      const knownUrls = new Set([this.legacy.product, ...(this.legacy.variants || []).map(variant => variant.url)].map(moxfieldUrl).filter(Boolean));
      // Only the deck's declared source links in its own description opt in.
      // Unrelated Moxfield references and other products are never rewritten.
      const scope = this.closest('[data-trc-product-id]');
      this.showLink = this.dataset.showLink !== 'false' && (this.dataset.fallback !== 'true' || !scope?.querySelector('trc-premade-deck-links:not([data-fallback="true"]):not([data-show-link="false"])'));
      this.descriptionLinks = [...(scope?.querySelectorAll('[data-trc-product-description] a') || [])]
        .filter(anchor => anchor.closest('[data-trc-product-id]') === scope && (originalDeckLinks.has(anchor) || knownUrls.has(moxfieldUrl(anchor.getAttribute('href')))))
        .map(anchor => {
          if (!originalDeckLinks.has(anchor)) originalDeckLinks.set(anchor, {anchor, href: anchor.getAttribute('href'), text: anchor.textContent, urlLabel: knownUrls.has(moxfieldUrl(anchor.textContent.trim()))});
          return originalDeckLinks.get(anchor);
        });
      this.onVersion = event => {
        if (String(event.detail?.variantId) === this.selectedId) this.apply(event.detail);
      };
      this.onVariant = () => {
        const form = this.dataset.staticVariant === 'true' ? null : document.getElementById(`product-form-${this.dataset.sectionId}`);
        const id = String(form?.querySelector('[name="id"]')?.value || this.dataset.variantId || '');
        if (id === this.selectedId) return;
        this.selectedId = id;
        if (this.dataset.versionedPremade === 'false') this.apply({managed:false});
        else { this.apply(null); read(id).catch(() => {}); }
      };
      document.addEventListener('variant:changed', this.onVariant);
      document.addEventListener('trc:premade-version', this.onVersion);
      this.onVariant();
    }
    apply(data) {
      syncInventory(this.closest('[data-trc-product-id]'), data, this.dataset.reorderEnabled === 'true');
      const rawUrl = data?.managed ? data.active?.decklistUrl : data ? (this.legacy.variants || []).find(variant => String(variant.id) === this.selectedId)?.url || this.legacy.product : '';
      const url = moxfieldUrl(rawUrl);
      this.hidden = !url || !this.showLink;
      this.anchor.hidden = !url;
      if (url) this.anchor.href = url;
      else this.anchor.removeAttribute('href');
      for (const entry of this.descriptionLinks) {
        if (data && !data.managed) {
          entry.anchor.setAttribute('href', entry.href);
          if (entry.urlLabel) entry.anchor.textContent = entry.text;
          entry.anchor.removeAttribute('aria-disabled');
        } else if (url) {
          entry.anchor.href = url;
          if (entry.urlLabel) entry.anchor.textContent = url;
          entry.anchor.removeAttribute('aria-disabled');
        } else {
          entry.anchor.removeAttribute('href');
          entry.anchor.setAttribute('aria-disabled', 'true');
          if (entry.urlLabel) entry.anchor.textContent = 'Deck link unavailable';
        }
      }
    }
    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariant);
      document.removeEventListener('trc:premade-version', this.onVersion);
      this.ready = false;
    }
  }
  customElements.define('trc-premade-deck-links', PremadeDeckLinks);
  customElements.define('trc-premade-version', PremadeVersion);
})();
